import React, { useState, useEffect } from "react";
import { User, X, Briefcase, FileText, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import CustomSelect from "../../../components/ui/CustomSelect";
import { useNotification } from "../../../context/NotificationContext";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import Input from "../../../components/ui/Input";
import { Button } from "../../../components/ui/button";

const TYPES_STRUCTURE = [
  { label: "Type de structure *", value: "" },
  { label: "Gestion de Portefeuille", value: "GP" },
  { label: "Prestataire de Services sur Actifs Numériques", value: "PSAN" },
  { label: "Banque", value: "Banque" },
  { label: "Assurance", value: "Assurance" },
  { label: "Broker", value: "Broker" },
  { label: "Autre", value: "Autre" }
];

const CLASSES_ACTIFS = [
  { label: "Classe d'actifs *", value: "" },
  { label: "Fonds Commun de Placement", value: "Fonds Commun de Placement" },
  { label: "Fonds d'Investissement Alternatif", value: "Fonds d'Investissement Alternatif" },
  { label: "Cryptomonnaies", value: "Cryptomonnaies" },
  { label: "Produits Structurés", value: "Produits Structurés" },
  { label: "Private Equity", value: "Private Equity" },
  { label: "Produits Immobiliers", value: "Produits Immobiliers" },
  { label: "Gestion Sous Mandat", value: "Gestion Sous Mandat" },
  { label: "BUFFER ETF", value: "BUFFER ETF" },
  { label: "Dette Privée", value: "Dette Privée" },
  { label: "Investissements Alternatifs", value: "Investissement Alternatif" },
  { label: "Services Exclusifs", value: "Services Exclusifs" },
  { label: "Assurance-Vie LUX", value: "Assurance-Vie LUX" }
];

const STATUTS = [
  { label: "Statut du Partenariat", value: "" },
  { label: "Actif", value: "Actif" },
  { label: "Inactif", value: "Inactif" },
  { label: "Onboarding", value: "Onboarding" }
];

export default function PartenaireForm({ partenaire, open, onClose, onSave }) {
  const { showToast } = useNotification();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [formData, setFormData] = useState({
    id: undefined,
    nom_societe: "",
    adresse: "",
    type_structure: "",
    classe_actifs: "",
    nom_referent: "",
    email: "",
    telephone: "",
    logo: "",
    numeroEnregistrement: "", 
    description: "", 
    siteWeb: "", 
    fonctionContact: "", 
    documentsRequis: [],
    statutConformite: "", 
    commentairesInternes: "", 
    dateAjout: new Date().toISOString(),
    volumeAffaires: 0,
    archived: false
  }); 

  useEffect(() => {
    if (partenaire) {
      setFormData({
        id: partenaire.id || undefined,
        nom_societe: partenaire.nom_societe || "",
        adresse: partenaire.adresse || "",
        type_structure: partenaire.type_structure || "",
        classe_actifs: partenaire.classe_actifs || "",
        nom_referent: partenaire.nom_referent || "",
        email: partenaire.email || "",
        telephone: partenaire.telephone || "",
        logo: partenaire.logo || "",
        numeroEnregistrement: partenaire.numeroEnregistrement || "",
        description: partenaire.description || "",
        siteWeb: partenaire.siteWeb || "",
        fonctionContact: partenaire.fonctionContact || "",
        documentsRequis: partenaire.documentsRequis || [],
        statutConformite: partenaire.statutConformite || "",
        commentairesInternes: partenaire.commentairesInternes || "",
        dateAjout: partenaire.dateAjout || new Date().toISOString(),
        volumeAffaires: partenaire.volumeAffaires || 0,
        archived: partenaire.archived ?? false
      });
    }
  }, [partenaire]); 

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handlePreSubmit = () => {
    const requiredFields = ["nom_societe", "type_structure", "email", "nom_referent", "adresse", "classe_actifs"];
    const missingFields = requiredFields.filter(field => !formData[field]);

    if (missingFields.length > 0) {
      showToast("Veuillez remplir tous les champs obligatoires.", "error");
      return;
    }
    
    setIsConfirmOpen(true);
  };

  const confirmSubmit = () => {
    setIsConfirmOpen(false);

    const cleanedData = { ...formData };
    Object.keys(cleanedData).forEach((key) => {
      if (cleanedData[key] === "" || cleanedData[key] === null) {
        cleanedData[key] = undefined;
      }
    });

    if (!cleanedData.id) {
      delete cleanedData.id;
    }

    onSave(cleanedData);
    showToast(`Partenaire ${partenaire ? "modifié" : "ajouté"} avec succès !`, "success");
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 sm:p-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-4xl bg-surface rounded-[var(--hp-radius-lg)] shadow-2xl flex flex-col overflow-hidden border border-[var(--hp-color-border-muted)] max-h-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--hp-color-border-muted)] bg-workspace/50 backdrop-blur-md sticky top-0 z-20">
            <div>
              <h2 className="text-xl font-bold text-primary">
                {partenaire ? "Modifier le partenaire" : "Ajouter un partenaire"}
              </h2>
              <p className="text-sm text-subtle mt-0.5">Renseignez les informations du partenaire ci-dessous.</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5 text-subtle hover:text-primary" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            {/* Upload du logo */}
            <div className="flex flex-col items-center mb-8">
              {formData.logo ? (
                <img
                  src={formData.logo}
                  alt="Logo"
                  className="h-20 w-20 object-contain rounded-xl shadow-hp-sm mb-4 border border-[var(--hp-color-border-muted)] bg-white p-2"
                />
              ) : (
                <div className="h-20 w-20 bg-surface-muted rounded-full flex items-center justify-center border border-[var(--hp-color-border-muted)] shadow-hp-sm mb-4">
                  <User className="h-10 w-10 text-subtle" />
                </div>
              )}
              <Input
                type="text"
                name="logo"
                placeholder="Lien du logo (URL)"
                className="max-w-sm text-center"
                value={formData.logo || ""}
                onChange={handleChange}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Colonne 1: Infos principales */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-4 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-secondary" />
                    Entreprise & Structure
                  </h3>
                  <div className="space-y-4">
                    <Input
                      type="text"
                      name="nom_societe"
                      placeholder="Nom du partenaire *"
                      value={formData.nom_societe}
                      onChange={handleChange}
                      required
                    />
                    
                    <CustomSelect
                      value={formData.type_structure}
                      onChange={(val) => handleSelectChange("type_structure", val)}
                      options={TYPES_STRUCTURE}
                    />

                    <CustomSelect
                      value={formData.classe_actifs}
                      onChange={(val) => handleSelectChange("classe_actifs", val)}
                      options={CLASSES_ACTIFS}
                    />
                    
                    <Input
                      type="text"
                      name="numeroEnregistrement"
                      placeholder="Numéro d'enregistrement"
                      value={formData.numeroEnregistrement || ""}
                      onChange={handleChange}
                    />

                    <CustomSelect
                      value={formData.statutConformite}
                      onChange={(val) => handleSelectChange("statutConformite", val)}
                      options={STATUTS}
                    />
                    
                    <div className="relative">
                      <Input
                        type="number"
                        name="volumeAffaires"
                        placeholder="Volume d'affaires"
                        className="pl-8"
                        value={formData.volumeAffaires || ""}
                        onChange={handleChange}
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle">€</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Colonne 2: Contact & Détails */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-4 flex items-center gap-2">
                    <User className="w-4 h-4 text-secondary" />
                    Contact & Adresse
                  </h3>
                  <div className="space-y-4">
                    <Input
                      type="text"
                      name="nom_referent"
                      placeholder="Nom du référent *"
                      value={formData.nom_referent}
                      onChange={handleChange}
                      required
                    />
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email *"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                    <Input
                      type="text"
                      name="telephone"
                      placeholder="Téléphone"
                      value={formData.telephone || ""}
                      onChange={handleChange}
                    />
                    <Input
                      type="text"
                      name="adresse"
                      placeholder="Adresse complète *"
                      value={formData.adresse}
                      onChange={handleChange}
                      required
                    />
                    <Input
                      type="text"
                      name="siteWeb"
                      placeholder="Site Web (ex: https://...)"
                      value={formData.siteWeb || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section pleine largeur: Description et notes */}
            <div className="mt-8 border-t border-[var(--hp-color-border-muted)] pt-6 space-y-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-secondary" />
                Notes & Documents
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <textarea
                  name="description"
                  placeholder="Description du partenariat..."
                  className="workspace-input min-h-[100px] resize-y"
                  value={formData.description || ""}
                  onChange={handleChange}
                />
                
                <textarea
                  name="commentairesInternes"
                  placeholder="Commentaires internes (visibles uniquement par l'équipe)..."
                  className="workspace-input min-h-[100px] resize-y bg-surface-muted"
                  value={formData.commentairesInternes || ""}
                  onChange={handleChange}
                />
              </div>

              <textarea
                name="documentsRequis"
                placeholder="Documents requis (séparés par une virgule)..."
                className="workspace-input"
                value={formData.documentsRequis.join(", ")}
                onChange={(e) => setFormData({ ...formData, documentsRequis: e.target.value.split(", ").filter(Boolean) })}
              />
            </div>
          </div>

          {/* Footer (sticky) */}
          <div className="px-6 py-4 bg-workspace/50 backdrop-blur-md border-t border-[var(--hp-color-border-muted)] flex justify-end gap-3 sticky bottom-0 z-20">
            <Button variant="secondary" onClick={onClose}>
              Annuler
            </Button>
            <Button variant="primary" onClick={handlePreSubmit}>
              <CheckCircle className="w-4 h-4 mr-2" />
              {partenaire ? "Modifier" : "Ajouter le partenaire"}
            </Button>
          </div>
        </motion.div>

        {/* Modal de Confirmation */}
        <ConfirmModal
          isOpen={isConfirmOpen}
          title={partenaire ? "Modifier le partenaire" : "Ajouter un partenaire"}
          message={partenaire ? "Êtes-vous sûr de vouloir modifier les informations de ce partenaire ?" : "Êtes-vous sûr de vouloir ajouter ce nouveau partenaire ?"}
          confirmText="Confirmer"
          cancelText="Annuler"
          type="info"
          onConfirm={confirmSubmit}
          onCancel={() => setIsConfirmOpen(false)}
        />
      </div>
    </AnimatePresence>
  );
}
